// src/App.jsx
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { authApi } from './services/api';
import { TextProvider, useTexts } from './context/TextContext';
import { ToastProvider } from './context/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import DocumentList from './pages/DocumentList';
import DocumentDetail from './pages/DocumentDetail';
import UserList from './pages/UserList';
import ReceiptProcessor from './pages/ReceiptProcessor';
import Landing from './pages/Landing';
import Notifications from './pages/Notifications';
import Setup from './pages/Setup';
import LoginAdmin from './pages/LoginAdmin';
import Pricing from './pages/Pricing';

// Loads texts in two steps:
// 1. On app start — uses browser language (public endpoint, no auth needed)
// 2. On login — uses Usr_idioma from DB (overrides browser lang if different)
// Detecta ?kac=<hash> en la URL y hace login automático
function KacHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithData } = useAuth();

  useEffect(() => {
    const kac = searchParams.get('kac');
    if (!kac) return;
    const doc = searchParams.get('doc');
    authApi.kacLogin(kac)
      .then(res => {
        const { token, user } = res.data;
        loginWithData(token, user);
        navigate(doc ? `/documents/${doc}` : '/documents', { replace: true });
      })
      .catch(() => navigate('/', { replace: true }));
  }, []);

  return null;
}

function TextLoader() {
  const { user } = useAuth();
  const { loadTexts, loadPublicTexts } = useTexts();

  // On mount: if already logged in use Usr_idioma, otherwise use browser language
  useEffect(() => {
    if (user) loadTexts();
    else loadPublicTexts();
  }, []);

  // On login: reload with Usr_idioma (overrides browser language)
  useEffect(() => {
    if (user) loadTexts();
  }, [user]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <TextProvider>
        <ToastProvider>
        <BrowserRouter>
          <TextLoader />
          <KacHandler />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />

            {/* Document list */}
            <Route path="/documents" element={
              <ProtectedRoute><DocumentList /></ProtectedRoute>
            } />

            {/* New document — must be before :id to avoid conflict */}
            <Route path="/documents/new" element={
              <ProtectedRoute><DocumentDetail /></ProtectedRoute>
            } />

            {/* Document detail / edit */}
            <Route path="/documents/:id" element={
              <ProtectedRoute><DocumentDetail /></ProtectedRoute>
            } />

            {/* Users admin */}
            <Route path="/users" element={
              <ProtectedRoute><UserList /></ProtectedRoute>
            } />

            {/* Receipt processor */}
            <Route path="/receipts" element={
              <ProtectedRoute><ReceiptProcessor /></ProtectedRoute>
            } />

            {/* Notification rules */}
            <Route path="/notifications" element={
              <ProtectedRoute><Notifications /></ProtectedRoute>
            } />

            {/* Setup / parametrization */}
            <Route path="/setup" element={
              <ProtectedRoute><Setup /></ProtectedRoute>
            } />

            {/* Admin login — bypasea mantenimiento, solo grupo 1 */}
            <Route path="/admin" element={<LoginAdmin />} />

            {/* Pricing — no vinculada desde ningún lado */}
            <Route path="/pricing" element={<Pricing />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ToastProvider>
      </TextProvider>
    </AuthProvider>
  );
}
